import { useState, type SubmitEvent } from 'react';
import { KeyRound, Lock, ShieldCheck } from 'lucide-react';
import { useAuth } from '../store/auth';
import { useUi } from '../store/ui';
import { brandMark, brandName, wordmark } from '../lib/assets';
import { Button, Field, TextInput } from '../components/ui';

const passwordBytes = (value: string) => new TextEncoder().encode(value).length;

export default function ChangePasswordPage() {
  const changePassword = useAuth((state) => state.changePassword);
  const pending = useAuth((state) => state.pending);
  const toast = useUi((state) => state.toast);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [requestError, setRequestError] = useState<string | null>(null);

  async function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    const next: Record<string, string> = {};
    const bytes = passwordBytes(newPassword);

    if (!currentPassword) next.currentPassword = 'Enter the temporary or current password.';
    if (bytes < 12 || bytes > 72) next.newPassword = 'Use a password between 12 and 72 bytes.';
    if (newPassword === currentPassword) next.newPassword = 'Choose a different password.';
    if (confirmation !== newPassword) next.confirmation = 'Passwords do not match.';

    setErrors(next);
    setRequestError(null);
    if (Object.keys(next).length > 0) return;

    const result = await changePassword(currentPassword, newPassword);
    if (!result.ok) {
      setRequestError(result.error);
      return;
    }
    toast('Password changed. Sign in again with your new password.');
  }

  return (
    <main className="flex min-h-dvh items-center justify-center bg-[#f6f4f0] px-5 py-10">
      <section className="w-full max-w-md rounded-2xl border border-[#e6e2da] bg-white p-6 shadow-[0_18px_60px_rgba(10,36,29,0.08)] sm:p-8">
        <div className="flex items-center gap-3">
          <img src={brandMark} alt="" aria-hidden className="h-9 w-auto" />
          <span aria-hidden className="h-7 w-px bg-[#e6e2da]" />
          <img src={wordmark} alt={brandName} className="h-9 w-auto" />
        </div>

        <span className="overline mt-8 block text-[#c69b3f]">Account security</span>
        <h1 className="mt-2 text-[26px] leading-tight font-bold tracking-[-0.025em] text-[#12241e]">
          Choose your own password
        </h1>
        <p className="mt-2 text-[13.5px] leading-relaxed text-[#5c6d64]">
          Your account was issued with a temporary password. Replace it before accessing customer
          or operational data.
        </p>

        <form onSubmit={submit} className="mt-7 flex flex-col gap-4" noValidate>
          <Field label="Current temporary password" required error={errors.currentPassword}>
            {(props) => (
              <TextInput
                {...props}
                type="password"
                autoComplete="current-password"
                autoFocus
                value={currentPassword}
                error={Boolean(errors.currentPassword)}
                onChange={(event) => {
                  setCurrentPassword(event.target.value);
                  setErrors({});
                  setRequestError(null);
                }}
              />
            )}
          </Field>

          <Field
            label="New password"
            required
            error={errors.newPassword}
            hint="12–72 bytes. A long, unique passphrase is easiest to remember."
          >
            {(props) => (
              <TextInput
                {...props}
                type="password"
                autoComplete="new-password"
                value={newPassword}
                error={Boolean(errors.newPassword)}
                onChange={(event) => {
                  setNewPassword(event.target.value);
                  setErrors({});
                  setRequestError(null);
                }}
              />
            )}
          </Field>

          <Field label="Confirm new password" required error={errors.confirmation}>
            {(props) => (
              <TextInput
                {...props}
                type="password"
                autoComplete="new-password"
                value={confirmation}
                error={Boolean(errors.confirmation)}
                onChange={(event) => {
                  setConfirmation(event.target.value);
                  setErrors({});
                  setRequestError(null);
                }}
              />
            )}
          </Field>

          {requestError ? (
            <p role="alert" className="rounded-xl bg-[#f8e7e4] px-3.5 py-2.5 text-[12.5px] font-semibold text-[#b4453a]">
              {requestError}
            </p>
          ) : null}

          <Button
            type="submit"
            size="md"
            fullWidth
            loading={pending}
            label={pending ? 'Changing password…' : 'Change password'}
            icon={!pending ? <KeyRound size={15} strokeWidth={2.2} aria-hidden /> : undefined}
          />
        </form>

        <p className="mt-5 flex gap-2 rounded-xl bg-[#eaf0ec] px-3.5 py-3 text-[12px] leading-relaxed text-[#33443c]">
          <ShieldCheck size={15} strokeWidth={2.2} className="mt-px shrink-0" aria-hidden />
          <span>After changing it, every existing session is revoked and you will sign in again.</span>
        </p>
        <span className="sr-only"><Lock aria-hidden /></span>
      </section>
    </main>
  );
}
